'use client'

/**
 * PrecedentRead - the per-name forward-outcome distribution on /stocks/[symbol] (register:
 * precedent-read-float-matched-forward-distribution-validated-1102). When the scanner currently reads a
 * name as a conviction A/B/C setup, this card answers "we have seen this before": across thousands of
 * past moves in names that profile like this one, the two-sided distribution of what they did in the next
 * 60 minutes - how high they reached AND how far they gave back. Honest by construction: it is a measured
 * base rate, never a prediction, and the downside always travels next to the upside.
 *
 * ⭐ IT LEADS WITH THE DOWNSIDE for tier A only (conviction-tier-a-loses-the-fair-float-control-1031): the
 * validated edge is that a fresh, weak-handed micro-float entry COLLAPSES LESS than other micro-float
 * movers, not that it runs more. B and C carry no edge claim and show the plain two-sided shape.
 *
 * Fetches /api/stocks/precedent (dark-gated server-side) and hides on null - a quiet or non-A/B/C name
 * has no precedent worth showing, exactly as TheScannerRead hides when the scanner has no read.
 */

import { useEffect, useState } from 'react'
import { Clock3 } from 'lucide-react'
import type { PrecedentReadData } from '@/lib/dashboard/precedentCohorts'

// Whole-number display: the percentiles are a multi-session base rate, and the per-week stability read
// (register precedent-read-...-1102) showed the tail stat (p90) wobbles ~+-2-3pts week to week, so a
// decimal would claim precision the data does not have. Stored precise in precedentCohorts.ts, rounded here.
const up = (v: number) => `${v >= 0 ? '+' : ''}${Math.round(v)}%`
const dn = (v: number) => `${Math.round(v)}%`

const TIER: Record<'A' | 'B' | 'C', { tag: string; sub: string; pill: string }> = {
  A: { tag: 'Top conviction', sub: 'the setup least likely to collapse, not the biggest mover', pill: 'bg-emerald-500/15 text-emerald-300 ring-1 ring-emerald-500/30' },
  B: { tag: 'High conviction', sub: 'a live low-float setup', pill: 'bg-teal-500/15 text-teal-300 ring-1 ring-teal-500/30' },
  C: { tag: 'Medium conviction', sub: 'a live low-float setup', pill: 'bg-zinc-500/15 text-zinc-200 ring-1 ring-zinc-500/30' },
}

export default function PrecedentRead({ symbol }: { symbol: string }) {
  const [data, setData] = useState<PrecedentReadData | null>(null)

  useEffect(() => {
    let alive = true
    fetch(`/api/stocks/precedent?symbol=${encodeURIComponent(symbol)}`, { cache: 'no-store' })
      .then((r) => (r.ok ? r.json() : null))
      .then((j) => { if (alive && j && j.precedent) setData(j.precedent as PrecedentReadData) })
      .catch(() => {})
    return () => { alive = false }
  }, [symbol])

  if (!data) return null
  const t = TIER[data.tier]
  if (!t) return null
  const c = data.cohort
  const e = data.edge

  return (
    <section aria-label="Precedent" className="mb-5 overflow-hidden rounded-2xl border border-zinc-800 bg-zinc-900/50 p-5 sm:p-6">
      <div className="flex items-center justify-between gap-3">
        <span className="text-[11px] font-medium uppercase tracking-[0.14em] text-zinc-500">Precedent</span>
        <span className="inline-flex items-center gap-1.5 text-[11px] text-zinc-500">
          <Clock3 className="h-3 w-3" />
          {data.sessions} sessions, through {data.asOf}
        </span>
      </div>

      <div className="mb-3 mt-3 flex flex-wrap items-center gap-x-3 gap-y-1">
        <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${t.pill}`}>{t.tag}</span>
        <span className="text-xs text-zinc-500">{t.sub}</span>
      </div>

      <p className="text-[13px] leading-snug text-zinc-300">
        We have logged <span className="font-semibold text-zinc-100">{c.n.toLocaleString()}</span> past moves in setups like this. In the next hour, they:
      </p>

      <div className="mt-3 text-[12px] font-medium text-emerald-300">Reached up to</div>
      <div className="mt-1.5 grid grid-cols-3 gap-2">
        {[['typical', c.up.p50], ['top 25%', c.up.p75], ['top 10%', c.up.p90]].map(([k, v]) => (
          <div key={k as string} className="rounded-lg bg-emerald-500/[0.08] px-2.5 py-2">
            <div className="text-[11px] text-emerald-300/80">{k}</div>
            <div className="text-[17px] font-semibold text-emerald-300">{up(v as number)}</div>
          </div>
        ))}
      </div>

      <div className="mt-3 text-[12px] font-medium text-rose-300">Gave back up to</div>
      <div className="mt-1.5 grid grid-cols-3 gap-2">
        <div className="rounded-lg bg-rose-500/[0.08] px-2.5 py-2">
          <div className="text-[11px] text-rose-300/80">typical</div>
          <div className="text-[17px] font-semibold text-rose-300">{dn(c.dn.p50)}</div>
        </div>
        <div className="rounded-lg bg-rose-500/[0.08] px-2.5 py-2">
          <div className="text-[11px] text-rose-300/80">worst 10%</div>
          <div className="text-[17px] font-semibold text-rose-300">{dn(c.dn.p10)}</div>
        </div>
        <div className="rounded-lg bg-zinc-800/60 px-2.5 py-2">
          <div className="text-[11px] text-zinc-500">tagged 5%</div>
          <div className="text-[13px] font-semibold text-zinc-300">{c.up5Pct}% up, {c.dn5Pct}% dn</div>
        </div>
      </div>

      {e && (
        <div className="mt-3 rounded-xl border border-emerald-500/20 bg-emerald-500/[0.06] p-3.5">
          <div className="text-sm font-semibold text-emerald-300">Collapses less than its float</div>
          <p className="mt-1 text-[12px] leading-snug text-zinc-300">
            Fresh, weak-handed entries like this hit -5% on <span className="font-medium text-zinc-100">{e.dropPct}%</span> of moves, against{' '}
            <span className="font-medium text-zinc-100">{e.controlDropPct}%</span> for other micro-float movers, on {e.daysBeat} of {e.daysTotal} sessions.
          </p>
        </div>
      )}

      <p className="mt-3.5 text-[11px] leading-snug text-zinc-500">
        {e
          ? 'Forward excursions over 60 minutes, not a path or a prediction. The upside is ordinary for a float this small; the lower collapse rate is the edge.'
          : 'Forward excursions over 60 minutes, not a path or a prediction. Both sides of the odds for setups like this.'}
      </p>
    </section>
  )
}
